import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBrand, BRAND_META } from "../context/BrandContext";
import { ROLE, roleLabel, homeFor } from "../lib/roles";
import Logo from "./Logo";
import { usePosEnabled } from "./pos/PosGate";
import {
    SquaresFour,
    Package,
    Gear,
    SignOut,
    CaretDown,
    Check,
    CalendarCheck,
    CashRegister,
    ChartBar,
} from "@phosphor-icons/react";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
} from "./ui/dropdown-menu";

const NAV = [
    {
        to: "/catalog", label: "Catalog", icon: SquaresFour, end: true, testId: "nav-catalog",
        roles: [ROLE.OWNER, ROLE.ADMIN_INPUT, ROLE.ADMIN_SALES, ROLE.SUPERVISOR, ROLE.ADMIN, ROLE.RESELLER, ROLE.DISTRIBUTOR],
    },
    {
        to: "/products", label: "Products", icon: Package, testId: "nav-products",
        roles: [ROLE.OWNER, ROLE.ADMIN_INPUT, ROLE.ADMIN_SALES, ROLE.SUPERVISOR, ROLE.ADMIN],
    },
    {
        to: "/pos", label: "POS", icon: CashRegister, testId: "nav-pos",
        roles: [ROLE.OWNER],
    },
    {
        to: "/purchases", label: "Purchases", icon: Package, testId: "nav-purchases",
        roles: [ROLE.OWNER],
    },
    {
        to: "/reporting", label: "Reporting", icon: CalendarCheck, testId: "nav-reporting",
        roles: [ROLE.OWNER, ROLE.FINANCE, ROLE.ADMIN_SALES, ROLE.SUPERVISOR],
    },
    {
        to: "/meta-ads", label: "Meta Ads", icon: ChartBar, testId: "nav-meta-ads",
        roles: [ROLE.META_ADS_VIEWER],
    },
    {
        to: "/settings", label: "Settings", icon: Gear, testId: "nav-settings",
        roles: [ROLE.OWNER, ROLE.ADMIN_INPUT, ROLE.ADMIN],
    },
];

export default function Layout() {
    const { user, logout } = useAuth();
    const { brands, accessible, current, setCurrent } = useBrand();
    const navigate = useNavigate();
    const wide = useLocation().pathname.startsWith("/pos");

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    const posOn = usePosEnabled();
    const items = NAV.filter((n) => n.roles.includes(user?.role) && (n.to !== "/pos" || posOn));
    const currentBrand = brands.find((b) => b.id === current) || BRAND_META[current] || null;
    const currentName = currentBrand?.name || (BRAND_META[current]?.name ?? "Catalog");
        const showSwitcher = accessible.length > 1;

    return (
        <div className="min-h-screen w-full max-w-full bg-ivory grain overflow-x-hidden">
            <header className="sticky top-0 z-40 bg-ivory/85 backdrop-blur-md border-b border-mist">
                <div className={`${wide ? "max-w-[1600px]" : "max-w-7xl"} mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between gap-3`}>
                    <Link to={homeFor(user?.role)} className="flex items-center gap-3 min-w-0" data-testid="brand-link">
                        <Logo height={36} className="md:hidden" />
                        <Logo height={44} className="hidden md:block" />
                    </Link>

                    <nav className="hidden md:flex items-center gap-1">
                        {items.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                data-testid={item.testId}
                                className={({ isActive }) =>
                                    `px-4 py-2 text-sm transition-colors ${
                                        isActive
                                            ? "text-ink border-b-2 border-sage-dark"
                                            : "text-neutral-500 hover:text-ink"
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2 md:gap-4 shrink-0">
                        {showSwitcher ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button
                                        data-testid="brand-switcher"
                                        className="inline-flex items-center gap-1.5 border border-mist rounded-full px-3 py-1 text-xs uppercase tracking-widest text-ink hover:border-sage-dark"
                                    >
                                        <span className="hidden sm:inline text-neutral-400">Brand ·</span>
                                        <span className="font-medium">{currentName}</span>
                                        <CaretDown size={12} weight="light" />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="min-w-[180px]">
                                    {brands
                                        .filter((b) => accessible.includes(b.id))
                                        .map((b) => (
                                            <DropdownMenuItem
                                                key={b.id}
                                                onClick={() => {
                                                    if (b.id === current) return;
                                                    setCurrent(b.id);
                                                    // Always exit any product/edit context when the
                                                    // active brand changes so the user never sees a
                                                    // product from the previous brand under the new
                                                    // theme. Replace, don't push, to avoid Back
                                                    // landing them back on the stale product page.
                                                    navigate("/catalog", { replace: true });
                                                }}
                                                data-testid={`brand-switch-${b.id}`}
                                                className="justify-between gap-2"
                                            >
                                                <span>{b.name}</span>
                                                {current === b.id && (
                                                    <Check size={14} weight="bold" className="text-sage-dark" />
                                                )}
                                            </DropdownMenuItem>
                                        ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            accessible.length === 1 && current && (
                                <span
                                    className="hidden sm:inline-flex items-center gap-1 border border-mist rounded-full px-3 py-0.5 text-[10px] uppercase tracking-widest text-neutral-500"
                                    data-testid="brand-badge"
                                >
                                    {currentName}
                                </span>
                            )
                        )}
                        {user && (
                            <span
                                className="hidden md:inline text-[10px] uppercase tracking-widest text-neutral-500 whitespace-nowrap"
                                data-testid="header-role"
                            >
                                {roleLabel(user.role)}
                            </span>
                        )}
                        <button
                            onClick={handleLogout}
                            data-testid="logout-button"
                            className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-ink"
                            aria-label="Log out"
                        >
                            <SignOut size={16} weight="light" />
                            <span className="hidden sm:inline">Log out</span>
                        </button>
                    </div>
                </div>
            </header>

            <main className={`relative z-10 ${wide ? "max-w-[1600px]" : "max-w-7xl"} mx-auto px-4 md:px-8 py-6 md:py-10 pb-28 md:pb-10 w-full`}>
                <Outlet />
            </main>

            {/* Mobile bottom nav — role-aware, hidden when nothing to show */}
            {items.length > 0 && (
                <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-mist">
                    <div className="grid" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
                        {items.map((item) => {
                            const Icon = item.icon;
                            return (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    end={item.end}
                                    data-testid={`mobile-${item.testId}`}
                                    className={({ isActive }) =>
                                        `flex flex-col items-center justify-center py-2.5 text-[10px] uppercase tracking-widest ${
                                            isActive ? "text-sage-dark" : "text-neutral-400"
                                        }`
                                    }
                                >
                                    <Icon size={20} weight="light" />
                                    <span className="mt-1 truncate max-w-full">{item.label}</span>
                                </NavLink>
                            );
                        })}
                    </div>
                </nav>
            )}
        </div>
    );
}
